import { useState } from "react";
import { CandidatesSearch } from "@/components/candidates/CandidatesSearch";
import { useCandidatesFilter } from "@/contexts/CandidatesFilterContext";
import { SavedQueriesDropdown, CreateSavedQueryForm } from "@/components/savedQueries";
import { useCreateSavedQuery, useUpdateSavedQuery, useTrackQueryExecution } from "@/hooks/savedQueries";
import { useAuth } from "@/providers/AuthProvider";
import { SavedQuery } from "@/types/savedQuery";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface CandidatesFilterWrapperProps {
  allSkills: string[];
  allStatuses: string[];
  onSkillsFilterOpen?: () => void;
}

export const CandidatesFilterWrapper = ({
  allSkills,
  allStatuses,
  onSkillsFilterOpen
}: CandidatesFilterWrapperProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSaveFormOpen, setIsSaveFormOpen] = useState(false);
  const createSavedQuery = useCreateSavedQuery();
  const updateSavedQuery = useUpdateSavedQuery();
  const trackExecution = useTrackQueryExecution();

  const {
    searchTerm,
    setSearchTerm,
    skillsFilter,
    setSkillsFilter,
    skillsOperator,
    setSkillsOperator,
    statusFilter,
    setStatusFilter,
    functionFilter,
    setFunctionFilter,
    showFavoritesOnly,
    setShowFavoritesOnly,
    currentPage,
    setCurrentPage,
    activeQueryId,
    setActiveQueryId,
    getCurrentFiltersAsQueryDefinition,
    applyQueryDefinition
  } = useCandidatesFilter();

  // Reset to first page when filters change
  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSkillsFilterChange = (skills: string[]) => {
    setSkillsFilter(skills);
    setCurrentPage(1);
  };

  const handleSkillsOperatorChange = (operator: "AND" | "OR") => {
    setSkillsOperator(operator);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (statuses: string[]) => {
    setStatusFilter(statuses);
    setCurrentPage(1);
  };

  const handleFunctionFilterChange = (functionName: string) => {
    setFunctionFilter(functionName);
    setCurrentPage(1);
  };

  const handleShowFavoritesOnlyChange = (show: boolean) => {
    setShowFavoritesOnly(show);
    setCurrentPage(1);
  };

  const handleApplySavedQuery = (query: SavedQuery) => {
    applyQueryDefinition(query.query_definition, query.id);
    trackExecution.mutate(query.id);
    toast({
      title: t("savedQueries.applied", "Query applied"),
      description: t("savedQueries.appliedDescription", "Filters have been updated"),
    });
  };

  const handleUpdateQuery = async (query: SavedQuery) => {
    const currentFilters = getCurrentFiltersAsQueryDefinition();
    await updateSavedQuery.mutateAsync({
      id: query.id,
      updates: {
        query_definition: currentFilters
      }
    });
    toast({
      title: t("savedQueries.updated", "Query updated"),
      description: t("savedQueries.updatedDescription", "Your saved query has been updated with current filters"),
    });
  };

  const handleSaveQuery = async (name: string, description?: string, isPublic?: boolean) => {
    const currentFilters = getCurrentFiltersAsQueryDefinition();
    await createSavedQuery.mutateAsync({
      name,
      description,
      query_definition: currentFilters,
      is_public: isPublic
    });
    setIsSaveFormOpen(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 items-start justify-between">
          <CandidatesSearch
            searchTerm={searchTerm}
            setSearchTerm={handleSearchTermChange}
            skillsFilter={skillsFilter}
            setSkillsFilter={handleSkillsFilterChange}
            skillsOperator={skillsOperator}
            setSkillsOperator={handleSkillsOperatorChange}
            statusFilter={statusFilter}
            setStatusFilter={handleStatusFilterChange}
            functionFilter={functionFilter}
            setFunctionFilter={handleFunctionFilterChange}
            showFavoritesOnly={showFavoritesOnly}
            setShowFavoritesOnly={handleShowFavoritesOnlyChange}
            allSkills={allSkills}
            allStatuses={allStatuses}
            onSkillsFilterOpen={onSkillsFilterOpen}
          />
          <SavedQueriesDropdown
            onApply={handleApplySavedQuery}
            onUpdate={handleUpdateQuery}
            onSaveClick={() => setIsSaveFormOpen(true)}
            currentUserId={user?.id}
            activeQueryId={activeQueryId}
          />
        </div>
      </div>

      <CreateSavedQueryForm
        isOpen={isSaveFormOpen}
        onClose={() => setIsSaveFormOpen(false)}
        onSubmit={handleSaveQuery}
        isLoading={createSavedQuery.isPending}
        currentFilters={getCurrentFiltersAsQueryDefinition()}
      />
    </>
  );
};
